const ISRAEL_TIMEZONE = "Asia/Jerusalem";

const DEFAULT_LOCALE = "he-IL";

/**
 * Format a date string for display in Israel timezone (full format: year, long month, day).
 * Use for success page, thank you page, email confirmation, admin.
 */
export function formatDateIsrael(
  dateString: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return new Date(dateString).toLocaleDateString(DEFAULT_LOCALE, {
    timeZone: ISRAEL_TIMEZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  });
}

/**
 * Format a date string for display in Israel timezone (short: dd/mm/yyyy).
 * Use for workshop detail calendar, payment page, admin dates list.
 */
export function formatDateIsraelShort(dateString: string): string {
  return new Date(dateString).toLocaleDateString(DEFAULT_LOCALE, {
    timeZone: ISRAEL_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** YYYY-MM-DD from local calendar date (avoids UTC shift from toISOString). */
export function formatLocalDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse YYYY-MM-DD as local midnight (not UTC). */
export function parseLocalDateString(dateString: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

/** Parse HH:mm (24h) to a Date for time-only pickers. */
export function parseTimeString(time: string): Date | null {
  if (!time) {
    return null;
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }
  const parsed = new Date();
  parsed.setHours(hours, minutes, 0, 0);
  return parsed;
}

/** Format Date to HH:mm (24h) for storage. */
export function formatTimeToHHmm(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** No-op hook for workshop date picker selection (debug logging removed for production). */
export function logWorkshopDatePickerSelection(
  _context: "add" | "edit",
  _picked: Date | null,
  _storedValue: string,
): void {
  // intentionally empty
}
